import type { LinkProps } from '@tanstack/react-router'
import type { ComponentType } from 'react'

import type { System, UserOf } from '@/lib/auth-store'
import type { MessageKey } from '@/lib/i18n/messages/en'

/** T13 sidebar section headers. */
export type NavGroup = 'operation' | 'branch' | 'restaurant' | 'platform'

export const NAV_GROUP_LABELS: Record<NavGroup, MessageKey> = {
  operation: 'nav.group.operation',
  branch: 'nav.group.branch',
  restaurant: 'nav.group.restaurant',
  platform: 'nav.group.platform',
}

/** What the sidebar / bottom bar render; registry order is priority (T13). */
export interface NavTarget {
  key: string
  /** Typed route path: adding an item requires its route to exist. */
  to: NonNullable<LinkProps['to']>
  icon: ComponentType<{ className?: string }>
  label: MessageKey
  group?: NavGroup
  /** Home pages need exact matching; other paths count children as active. */
  exact?: boolean
  /** Values for the `$` segments of `to` (restaurant-scoped pages, E6). */
  params?: Record<string, string>
}

export interface NavItem<S extends System = System> extends NavTarget {
  /** Role filter; absent = every logged-in user. E1 roles plug in here. */
  visible?: (user: UserOf<S>) => boolean
}

/** Anonymous (a session dying mid-visit) gets no destinations, only the top bar. */
export function visibleNavItems<S extends System>(
  items: readonly NavItem<S>[],
  user: UserOf<S> | undefined,
): NavTarget[] {
  if (!user) return []
  return items.filter((item) => item.visible?.(user) ?? true)
}

export const BAR_SLOTS = 4

/** T13 count rule: ≤4 items fill the bar; else top 3 + a More slot holding the rest. */
export function barSlots(items: readonly NavTarget[]): { bar: NavTarget[]; overflow: NavTarget[] } {
  if (items.length <= BAR_SLOTS) return { bar: [...items], overflow: [] }
  return { bar: items.slice(0, BAR_SLOTS - 1), overflow: items.slice(BAR_SLOTS - 1) }
}

export interface NavSection {
  group: NavGroup | undefined
  items: NavTarget[]
}

/** Buckets items by group, sections ordered by each group's first appearance. */
export function navSections(items: readonly NavTarget[]): NavSection[] {
  const sections: NavSection[] = []
  const byGroup = new Map<NavGroup | undefined, NavSection>()
  for (const item of items) {
    let section = byGroup.get(item.group)
    if (!section) {
      section = { group: item.group, items: [] }
      byGroup.set(item.group, section)
      sections.push(section)
    }
    section.items.push(item)
  }
  return sections
}

/** The concrete pathname `to` + `params` produce; unknown params stay literal. */
export function navPath(item: Pick<NavTarget, 'to' | 'params'>): string {
  return item.to.replace(/\$(\w+)/g, (segment, key: string) => item.params?.[key] ?? segment)
}

/** Mirrors the router's active-link rule for slots that are not one Link (More). */
export function isItemActive(pathname: string, item: Pick<NavTarget, 'to' | 'exact' | 'params'>): boolean {
  const clean = pathname.replace(/\/+$/, '') || '/'
  const target = navPath(item)
  if (item.exact) return clean === target
  return clean === target || clean.startsWith(`${target}/`)
}

/** The deepest item the pathname falls under — the page's name for the tab title; a sub-page counts as its parent. */
export function activeNavItem<T extends Pick<NavTarget, 'to' | 'exact' | 'params'>>(pathname: string, items: readonly T[]): T | undefined {
  let best: T | undefined
  for (const item of items) {
    if (isItemActive(pathname, item) && (!best || navPath(item).length > navPath(best).length)) best = item
  }
  return best
}

const COLLAPSED_KEY = 'romi.nav-collapsed'

/** Collapsed icon rail, persisted per device (T13). */
export function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

export function writeSidebarCollapsed(collapsed: boolean) {
  try {
    if (collapsed) localStorage.setItem(COLLAPSED_KEY, '1')
    else localStorage.removeItem(COLLAPSED_KEY)
  } catch {
    // Storage blocked: the rail just re-expands next visit.
  }
}
